import { useMutation } from '@apollo/client';
import { Col, notification, Radio, Row } from 'antd';
import { RadioChangeEvent } from 'antd/lib/radio';
import AddressFormatComponent from 'components/common/AddressFormatComponent';
import { InputComponent } from 'components/common/Input';
import { LabelStyled } from 'components/common/Input/controlled/styled';
import { INPUT_TYPE, LABEL_TYPE } from 'components/common/Input/types';
import InputFieldsGenerator from 'components/InputFieldsGenerator';
import { DataFieldProps } from 'components/InputFieldsGenerator/types';
import { usStatesSelectOptions } from 'constants/usStates';
import { Form, Formik } from 'formik';
import useFormikRefHook from 'hooks/useFormikRefHook';
import React, { useMemo, useState } from 'react';
import { useRouteMatch } from 'react-router';
import ROUTES from 'routes/routeConstants';
import { UPDATE_USER } from 'services/graphql/mutations/users';
import { GET_SINGLE_USER } from 'services/graphql/queries/user.query';
import { IPaymentAddress } from 'types/user';
import { formatAddress } from 'utils';
import IntlMessages from 'utils/IntlMessages';
import * as Yup from 'yup';

import PortalProfileSaveButton from '../PortalProfileSaveButton';
import { FormActionButtonStyled, SideFieldContainerColStyled } from '../styled';
import { InputProps, ITabProps } from '../types';
import { MailingAddressOption } from './types';

export const addressValidationObject = {
  address1: Yup.string().max(46, 'Maximum length is 46').required('Required'),
  address2: Yup.string().max(46, 'Maximum length is 46').notRequired(),
  city: Yup.string().max(50, 'Maximum length is 50').required('Required'),
  state: Yup.string().required('Required'),
  zip: Yup.string()
    .matches(/(^\d{5}$)|(^\d{9}$)|(^\d{5}-\d{4}$)/, 'Wrong format')
    .required('Required'),
};

export const addressFields: InputProps[] = [
  {
    label: 'Address Line 1',
    name: 'address1',
    addressSuggestion: true,
    addressSuggestionSubFields: {
      address1: 'address1',
      city: 'city',
      zip: 'zip',
      state: 'state',
    },
  },
  { label: 'Address Line 2', name: 'address2' },
  { label: 'City', name: 'city' },
  {
    label: 'Zip code',
    name: 'zip',
    sideFields: [
      {
        label: 'State',
        name: 'state',
        type: INPUT_TYPE.SELECT,
        inputOptions: usStatesSelectOptions,
        size: 8,
      },
    ],
  },
];

const Payment: React.FC<ITabProps> = ({
  user,
  hideSaveButton,
  portalButtonClassName,
  submitButtonLabel,
  onSubmit,
  onSubmitCallback,
  showNotificationOnSuccess = true,
  showNotificationOnFailure = true,
}) => {
  const isSetupWizard = !!useRouteMatch({
    exact: true,
    path: ROUTES.USER_WIZARD,
  })?.isExact;
  const containerClass =
    portalButtonClassName || (isSetupWizard ? 'steps-content' : '.custom-tabs-content-active .gx-tab-content-header');

  // user address
  const { email } = user;
  const fullName = `${user.firstName} ${user.lastName}`;
  const fullAddress = formatAddress(user);
  const userPaymentAddress = user.paymentAddress;
  // payment address
  const pmAddress = formatAddress(user.paymentAddress);

  const defaultOption =
    !pmAddress || pmAddress === fullAddress ? MailingAddressOption.PRIMARY : MailingAddressOption.NEW;

  const [selectedOption, setSelectedOption] = useState(defaultOption);
  const [loading, setLoading] = useState(false);

  const baseInitFields = {
    mailedCheck: 'mailedCheck',
    payableName: userPaymentAddress?.payableName || fullName || '',
  };
  const withNewAddressInitFields = {
    ...baseInitFields,
    address1: userPaymentAddress?.address1 || '',
    address2: userPaymentAddress?.address2 || '',
    city: userPaymentAddress?.city || '',
    zip: userPaymentAddress?.zip || '',
    state: userPaymentAddress?.state || '',
  };

  const baseValidationObject = useMemo(() => ({ payableName: Yup.string().required('Required') }), []);
  const newAddressValidationObject = useMemo(() => ({ ...baseValidationObject, ...addressValidationObject }), [
    selectedOption,
  ]);

  const ref = useFormikRefHook('payment');

  const [updateUser] = useMutation(UPDATE_USER, {
    refetchQueries: [
      {
        query: GET_SINGLE_USER,
        variables: {
          email,
        },
      },
    ],
  });

  const handleChooseMailingAddress = (event: RadioChangeEvent) => {
    setSelectedOption(event.target.value);
  };

  const updateUserPayment = async (paymentAddressDetail: IPaymentAddress) => {
    try {
      await updateUser({
        variables: {
          filter: {
            id: user.id,
          },
          updatedDoc: {
            paymentAddress: paymentAddressDetail,
          },
        },
      });

      console.log('updated payment');
      // UserAction.fetchUserSuccess({
      //   paymentAddress: paymentAddressDetail,
      // });
      if (showNotificationOnSuccess)
        notification.success({
          message: 'Update payment successfully!',
          key: 'updateProfile',
          duration: 2,
        });
    } catch (error) {
      if (showNotificationOnFailure)
        notification.error({
          message: 'Update payment failed!',
          key: 'updateProfile',
          duration: 2,
        });
    } finally {
      setLoading(false);
    }
  };

  const getAddressToUpdate = (formDto: IPaymentAddress, option: MailingAddressOption): IPaymentAddress => {
    if (option === MailingAddressOption.NEW) {
      return {
        address1: formDto.address1,
        address2: formDto.address2,
        city: formDto.city,
        mailedCheck: formDto.mailedCheck,
        payableName: formDto.payableName,
        state: formDto.state,
        zip: formDto.zip,
      };
    }
    return {
      ...formDto,
      address1: user.address1,
      address2: user.address2,
      city: user.city,
      state: user.state,
      zip: user.zip,
    };
  };

  const onSubmitPayment = (value: any) => {
    if (onSubmit) {
      onSubmit(value);
      return;
    }
    setLoading(true);
    const paymentAddressDetail: IPaymentAddress = getAddressToUpdate(value, selectedOption);
    updateUserPayment(paymentAddressDetail);
    onSubmitCallback?.(value);
  };

  const primaryAddressData: DataFieldProps[] = [
    {
      label: 'Address',
      value: <AddressFormatComponent addressDetail={user} separateLines />,
    },
  ];

  return (
    <div className='gx-signin-form payment-address'>
      <Formik
        initialValues={selectedOption === MailingAddressOption.PRIMARY ? baseInitFields : withNewAddressInitFields}
        onSubmit={onSubmitPayment}
        validationSchema={
          selectedOption === MailingAddressOption.PRIMARY
            ? Yup.object(baseValidationObject)
            : Yup.object(newAddressValidationObject)
        }
        innerRef={ref}
        validateOnBlur>
        {(props) => (
          <Form className='gx-signin-form gx-form-row0 gx-payment-form'>
            <Row>
              <Col span={24}>
                <SideFieldContainerColStyled
                  className=''
                  style={{ paddingLeft: 0, paddingRight: 32 }}
                  span={24}
                  lg={24}>
                  <InputComponent
                    style={{ marginBottom: 20 }}
                    type={INPUT_TYPE.RADIO}
                    name='mailedCheck'
                    label='Choose Payment Method'
                    inputOptions={[{ value: 'mailedCheck', label: 'Mailed Check' }]}
                    labelType={LABEL_TYPE.above}
                    defaultValue='mailedCheck'
                  />
                </SideFieldContainerColStyled>

                <SideFieldContainerColStyled
                  className=''
                  style={{ paddingLeft: 0, paddingRight: 32 }}
                  span={24}
                  lg={24}>
                  <InputComponent
                    type={INPUT_TYPE.TEXT}
                    name='payableName'
                    label='Make Check Payable To'
                    style={{ marginBottom: 20 }}
                    labelType={LABEL_TYPE.above}
                  />
                </SideFieldContainerColStyled>

                <LabelStyled $labelType={LABEL_TYPE.above}>
                  <div>Check Mailing Address</div>
                </LabelStyled>
                <Row className='gx-payment-row-field'>
                  <Col span={24} className='gx-px-0'>
                    <Radio.Group
                      className='gx-payment-mailing-option gx-mb-3'
                      onChange={handleChooseMailingAddress}
                      value={selectedOption}>
                      <Row>
                        <Col span={10} md={12} className='gx-px-0'>
                          <Radio className='option-button' value={MailingAddressOption.PRIMARY}>
                            Use Primary Address
                          </Radio>
                        </Col>
                        <Col span={14} md={12} className='gx-pr-0'>
                          <Radio className='option-button' value={MailingAddressOption.NEW}>
                            Specify Payment Mailing Address
                          </Radio>
                        </Col>
                      </Row>
                    </Radio.Group>
                  </Col>
                  {selectedOption === MailingAddressOption.PRIMARY ? (
                    <div style={{ margin: '0 -16px' }} className='gx-w-100'>
                      <InputFieldsGenerator dataFields={primaryAddressData} />
                    </div>
                  ) : (
                    <Col style={{ margin: '0 -16px' }} className='side-field option-child' span={24}>
                      <InputFieldsGenerator formFields={addressFields} />
                    </Col>
                  )}
                </Row>
              </Col>
            </Row>
            <Row justify='end' className={hideSaveButton ? 'gx-d-none' : ''}>
              <Col span={24}>
                <Row justify='start'>
                  <PortalProfileSaveButton containerClass={containerClass}>
                    <FormActionButtonStyled
                      isLoading={loading}
                      buttonType='primary'
                      htmlType='button'
                      className={`gx-mb-0 gx-mr-0 ${hideSaveButton ? 'gx-d-none' : ''}`}
                      onClick={() => {
                        props.handleSubmit();
                      }}>
                      {submitButtonLabel || <IntlMessages id='userProfile.saveProfile' />}
                    </FormActionButtonStyled>
                  </PortalProfileSaveButton>
                </Row>
              </Col>
            </Row>
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default React.memo(Payment);
